import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { AlertIcon, BanIcon, CheckIcon, XIcon } from '../../ui/icons';

/**
 * KOMUNIKAT ULOTNY (toast): jeden sposob na „zapisano" i „nie udalo sie".
 *
 * Powstal, bo w panelu bylo 58 wywolan `alert()` w 6 plikach (faktury, platnosci, rezerwacje,
 * super-admin), a obok nich strony pokazujace TEN SAM komunikat jako wlasny baner. `alert()` jest
 * zly z czterech powodow naraz i kazdy z nich sam wystarczylby za argument:
 *   - blokuje watek, wiec do klikniecia „OK" aplikacja stoi (razem z trwajacymi zapytaniami);
 *   - rysuje go przegladarka, wiec nie zna ani motywu ciemnego, ani jezyka aplikacji;
 *   - na telefonie wyglada jak komunikat systemowy, czyli jak awaria, a nie jak potwierdzenie;
 *   - nie da sie w nim umiescic akcji („Cofnij", „Pokaz fakture").
 *
 * Ten plik NIE jest jeszcze nigdzie podpiety — `ToastProvider` wchodzi do `App.js` w kolejnej fali,
 * a strony migruja z `alert()` osobno. Dlatego `useToast()` wywolany bez providera NIE moze wywalic
 * aplikacji (patrz `ZASTEPCZE_API` nizej): w trakcie migracji beda istniec strony, ktore juz wolaja
 * hook, choc drzewo nad nimi jeszcze go nie zapewnia.
 */

/* Ponad cztery komunikaty naraz to juz nie komunikat, tylko zaslona: petla bledow (np. zapis 12
   pozycji, kazda z bledem) zakrylaby caly ekran razem z przyciskiem, ktorym mozna to przerwac.
   Wypada NAJSTARSZY — nowszy komunikat prawie zawsze dotyczy tego, co uzytkownik robi TERAZ. */
export const MAX_TOASTS = 4;

/* Blad dostaje 7 s, reszta 4 s. Powod jest praktyczny, nie estetyczny: komunikat udany potwierdza
   cos, czego uzytkownik sie spodziewa, a blad trzeba PRZECZYTAC i czesto zapamietac (numer
   dokumentu, nazwa pola). Cztery sekundy sa na to za krotkie. */
export const DEFAULT_DURATIONS = {
  success: 4000,
  info: 4000,
  warning: 4000,
  error: 7000,
};

/* Czas wyjscia odpowiada tokenowi `--dur` (0.2 s) z index.css. JS nie czyta tokenow CSS bez
   `getComputedStyle`, wiec liczba jest tu powtorzona SWIADOMIE i sluzy wylacznie za bezpiecznik:
   usuniecie ze stanu robi `onAnimationEnd`, a ten licznik ratuje sytuacje, w ktorej zdarzenie nie
   przyjdzie (kafelek schowany, karta w tle). Rozjazd o kilkadziesiat milisekund nic tu nie psuje. */
const CZAS_WYJSCIA_MS = 260;

/* Wariant komunikatu -> rodzina tokenow znaczeniowych z index.css. Mapa istnieje, bo nazwa wariantu
   w API (`error`) celowo nie jest nazwa tokenu (`--danger`): API opisuje ZNACZENIE dla wolajacego,
   tokeny opisuja kolor. Te tokeny maja osobne wartosci dla trybu ciemnego, wiec komponent nie
   dorzuca zadnego wlasnego koloru. */
const TOKEN_STANU = {
  success: 'success',
  error: 'danger',
  warning: 'warning',
  info: 'info',
};

// W `src/ui/icons.js` nie ma ikony informacji, a tamten plik nalezy do innego zadania tej fali,
// wiec nie dokladam do niego eksportu. Rysunek trzyma jego konwencje: 24x24, stroke na currentColor.
// Emotki sa w tym projekcie zakazane — nie dziedzicza motywu, a czytnik ekranu odczytuje ich pelna
// nazwe posrodku zdania (patrz naglowek src/ui/icons.js).
const InfoIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    focusable="false"
    style={{ flexShrink: 0 }}
  >
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path
      d="M12 11v5M12 8h.01"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* Blad dostaje przekreslone kolo, ostrzezenie trojkat — inaczej oba stany roznilby WYLACZNIE
   kolor, a to jedyna roznica, ktorej czesc osob nie widzi. */
const IKONA_STANU = {
  success: CheckIcon,
  error: BanIcon,
  warning: AlertIcon,
  info: InfoIcon,
};

/* Bledy i ostrzezenia czyta sie `assertive` (przerywaja to, co czytnik mowi w tej chwili), reszta
   `polite`. Jeden pojemnik nie moze miec dwoch wartosci `aria-live`, a obszar aktywny musi ISTNIEC
   zanim wpadnie do niego tresc — stad dwa pojemniki, oba obecne od montowania, zamiast jednego
   wstawianego razem z komunikatem (taki bywa przez czytniki pomijany). */
const PILNE_WARIANTY = ['error', 'warning'];

/* ── CZYSTY STAN LISTY ───────────────────────────────────────────────────────────────────────────
   Wydzielone z komponentu, zeby dalo sie je pokryc testem: w projekcie NIE MA @testing-library,
   wiec test renderujacy komponent nie istnieje, a cala logika, ktora ma byc sprawdzona, musi dac
   sie wywolac jak zwykla funkcja. */

/** Dokladanie komunikatu z limitem: przy przepelnieniu wypada NAJSTARSZY. */
export function pushToast(list, toast) {
  return [...list, toast].slice(-MAX_TOASTS);
}

export function removeToastById(list, id) {
  return list.filter((t) => t.id !== id);
}

/** Znacznik „wychodzi": kafelek zostaje w drzewie na czas animacji wyjscia i dopiero potem znika. */
export function markToastLeaving(list, id) {
  if (!list.some((t) => t.id === id && !t.leaving)) return list;
  return list.map((t) => (t.id === id ? { ...t, leaving: true } : t));
}

/**
 * `duration: 0` znaczy „nie znika samo" i musi przejsc, mimo ze 0 jest falsywe — stad jawne
 * porownanie zamiast `options.duration || domyslny`. Wartosc bezsensowna (ujemna, NaN, tekst)
 * traktujemy jak brak, bo komunikat wiszacy w nieskonczonosc przez pomylke jest gorszy niz krotki.
 */
export function resolveDuration(variant, options = {}) {
  const podana = options ? options.duration : undefined;
  if (podana === 0) return 0;
  if (typeof podana === 'number' && Number.isFinite(podana) && podana > 0) return podana;
  return DEFAULT_DURATIONS[variant] !== undefined
    ? DEFAULT_DURATIONS[variant]
    : DEFAULT_DURATIONS.info;
}

let licznikId = 0;
const kolejneId = () => {
  licznikId += 1;
  return `toast-${licznikId}`;
};

/* ── WYGLAD ──────────────────────────────────────────────────────────────────────────────────── */

const wjazdZBoku = keyframes`
  from { opacity: 0; transform: translate3d(24px, 0, 0); }
  to   { opacity: 1; transform: translate3d(0, 0, 0); }
`;

const wyjazdWBok = keyframes`
  from { opacity: 1; transform: translate3d(0, 0, 0); }
  to   { opacity: 0; transform: translate3d(24px, 0, 0); }
`;

const wjazdZDolu = keyframes`
  from { opacity: 0; transform: translate3d(0, 16px, 0); }
  to   { opacity: 1; transform: translate3d(0, 0, 0); }
`;

const wyjazdWDol = keyframes`
  from { opacity: 1; transform: translate3d(0, 0, 0); }
  to   { opacity: 0; transform: translate3d(0, 16px, 0); }
`;

/* Warstwa nie lapie klikniec (`pointer-events: none`) — inaczej niewidzialny prostokat w rogu
   zjadalby klikniecia w tresc pod spodem takze wtedy, gdy nie ma zadnego komunikatu. Klikalne sa
   dopiero same kafelki. */
/* --help-fab-space rezerwuje przycisk pomocy (components/help/HelpButton.js) stojacy w tym samym
   rogu: stos powiadomien staje NAD nim, zamiast go zaslaniac. Bez przycisku zmiennej nie ma. */
const Warstwa = styled.div`
  position: fixed;
  right: calc(var(--sp-5) + var(--safe-right));
  bottom: calc(var(--sp-5) + var(--safe-bottom) + var(--help-fab-space, 0px));
  z-index: var(--z-toast);
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  width: min(380px, calc(100vw - var(--sp-8)));
  pointer-events: none;

  /* Na telefonie kafelek 380px w rogu jest za waski na zdanie i za daleko od kciuka; pelna szerokosc
     u dolu to standard tej platformy. Token --safe-bottom trzyma go nad kreska gestu.
     (W komentarzu WEWNATRZ szablonu styled nie moze byc odwrotnego apostrofu: zamknalby szablon,
     a to, co dalej, parser czytalby jako JS.) */
  @media (max-width: 768px) {
    left: calc(var(--sp-3) + var(--safe-left));
    right: calc(var(--sp-3) + var(--safe-right));
    bottom: calc(var(--sp-3) + var(--safe-bottom) + var(--help-fab-space, 0px));
    width: auto;
  }
`;

const Grupa = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
`;

const Kafelek = styled.div`
  --stan: var(--${(p) => TOKEN_STANU[p.$wariant]});
  --stan-bg: var(--${(p) => TOKEN_STANU[p.$wariant]}-bg);
  --stan-border: var(--${(p) => TOKEN_STANU[p.$wariant]}-border);

  display: flex;
  align-items: flex-start;
  gap: var(--sp-3);
  padding: var(--sp-3) var(--sp-4);
  background: var(--stan-bg);
  border: 1px solid var(--stan-border);
  border-left: 4px solid var(--stan);
  border-radius: var(--r-md);
  box-shadow: var(--shadow-lg);
  color: var(--text);
  pointer-events: auto;

  /* Reguly prefers-reduced-motion NIE powtarzam — index.css scina animacje globalnie. */
  animation: ${(p) => (p.$wychodzi ? wyjazdWBok : wjazdZBoku)} var(--dur) var(--ease) both;

  @media (max-width: 768px) {
    animation-name: ${(p) => (p.$wychodzi ? wyjazdWDol : wjazdZDolu)};
  }
`;

const Ikona = styled.span`
  display: flex;
  color: var(--stan);
  /* Wyrownanie do PIERWSZEGO wiersza tekstu, nie do srodka kafelka: przy dwoch wierszach tresci
     ikona posrodku wyglada na przesunieta. */
  margin-top: 1px;
`;

const Tresc = styled.div`
  flex: 1;
  min-width: 0;
`;

const Tytul = styled.div`
  margin-bottom: var(--sp-1);
  font-size: var(--fs-base);
  font-weight: 700;
  color: var(--stan);
`;

const Wiadomosc = styled.div`
  font-size: var(--fs-sm);
  color: var(--text);
  /* Komunikaty z serwera potrafia niesc dlugi identyfikator bez spacji — bez tego rozpychalby
     kafelek poza ekran. */
  overflow-wrap: anywhere;
`;

const PrzyciskAkcji = styled.button`
  margin-top: var(--sp-2);
  padding: var(--sp-1) var(--sp-3);
  font-size: var(--fs-sm);
  font-weight: 700;
  color: var(--stan);
  background: transparent;
  border: 1px solid var(--stan-border);
  border-radius: var(--r-sm);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease);

  &:hover { background: var(--hover-bg); }
`;

const PrzyciskZamkniecia = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-1);
  color: var(--text-muted);
  background: transparent;
  border: none;
  border-radius: var(--r-sm);
  cursor: pointer;
  transition: color var(--dur-fast) var(--ease), background var(--dur-fast) var(--ease);

  &:hover { color: var(--text); background: var(--hover-bg); }
`;

/* ── POJEDYNCZY KOMUNIKAT ────────────────────────────────────────────────────────────────────── */

function Komunikat({ toast, onDismiss, onPause, onResume, onExited }) {
  const Ikonka = IKONA_STANU[toast.variant] || InfoIcon;

  const zakonczonaAnimacja = (e) => {
    /* Interesuje nas animacja SAMEGO kafelka; gdyby cokolwiek w srodku dostalo kiedys wlasna
       animacje, jej `animationend` przebulbowalby tutaj i zdjal komunikat przed czasem. */
    if (e.target !== e.currentTarget) return;
    if (toast.leaving) onExited(toast.id);
  };

  return (
    <Kafelek
      $wariant={toast.variant}
      $wychodzi={toast.leaving}
      onMouseEnter={() => onPause(toast.id, 'kursor')}
      onMouseLeave={() => onResume(toast.id, 'kursor')}
      /* `onFocus`/`onBlur` w Reakcie bulbuja, wiec wystarczaja na pojemniku: lapia fokus przyciskow
         w srodku. Bez tego komunikat znikalby osobie, ktora wlasnie doszla do niego tabulatorem. */
      onFocus={() => onPause(toast.id, 'fokus')}
      onBlur={() => onResume(toast.id, 'fokus')}
      onAnimationEnd={zakonczonaAnimacja}
    >
      <Ikona>
        <Ikonka size={20} />
      </Ikona>

      <Tresc>
        {toast.title ? <Tytul>{toast.title}</Tytul> : null}
        <Wiadomosc>{toast.message}</Wiadomosc>
        {toast.action ? (
          <PrzyciskAkcji
            type="button"
            onClick={() => {
              toast.action.onClick();
              /* Akcja jest jedynym powodem, dla ktorego ten komunikat jeszcze wisi — po jej
                 wykonaniu nie ma na co czekac. */
              onDismiss(toast.id);
            }}
          >
            {toast.action.label}
          </PrzyciskAkcji>
        ) : null}
      </Tresc>

      <PrzyciskZamkniecia type="button" aria-label="Zamknij" onClick={() => onDismiss(toast.id)}>
        <XIcon size={16} />
      </PrzyciskZamkniecia>
    </Kafelek>
  );
}

/* ── PROVIDER ────────────────────────────────────────────────────────────────────────────────── */

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasty, setToasty] = useState([]);

  /* Trzy referencje zamiast stanu, bo zaden z tych zapisow NIE ma odrysowywac ekranu:
       licznikiRef        — odliczanie do samoczynnego zniknieca (z pauza),
       licznikiWyjsciaRef — bezpiecznik na wypadek braku `animationend`,
       toastyRef          — biezaca lista dla `dismissAll`, ktore musi znac id, a nie chce byc
                            przeliczane przy kazdej zmianie listy (inaczej caly `api` z `useMemo`
                            zmienialby tozsamosc po kazdym komunikacie i unieważnial efekty stron). */
  const licznikiRef = useRef(new Map());
  const licznikiWyjsciaRef = useRef(new Map());
  const toastyRef = useRef([]);

  useEffect(() => {
    toastyRef.current = toasty;
  }, [toasty]);

  const wyczyscLicznik = useCallback((id) => {
    const rec = licznikiRef.current.get(id);
    if (rec && rec.timeoutId) clearTimeout(rec.timeoutId);
    licznikiRef.current.delete(id);
  }, []);

  const dismiss = useCallback(
    (id) => {
      if (!id) return;
      wyczyscLicznik(id);
      /* Podwojne zamkniecie (klik w „Zamknij" plus akcja, ktora tez zamyka) nie moze wystartowac
         drugiego bezpiecznika — ten obudzilby sie juz po usunieciu wpisu. */
      if (licznikiWyjsciaRef.current.has(id)) return;
      setToasty((prev) => markToastLeaving(prev, id));
      const t = setTimeout(() => {
        licznikiWyjsciaRef.current.delete(id);
        setToasty((prev) => removeToastById(prev, id));
      }, CZAS_WYJSCIA_MS);
      licznikiWyjsciaRef.current.set(id, t);
    },
    [wyczyscLicznik]
  );

  const zdejmijPoAnimacji = useCallback((id) => {
    const t = licznikiWyjsciaRef.current.get(id);
    if (t) clearTimeout(t);
    licznikiWyjsciaRef.current.delete(id);
    setToasty((prev) => removeToastById(prev, id));
  }, []);

  const dismissAll = useCallback(() => {
    toastyRef.current.forEach((t) => dismiss(t.id));
  }, [dismiss]);

  const uruchomOdliczanie = useCallback(
    (id) => {
      const rec = licznikiRef.current.get(id);
      if (!rec || rec.wstrzymane.size > 0 || rec.pozostalo <= 0) return;
      rec.start = Date.now();
      rec.timeoutId = setTimeout(() => dismiss(id), rec.pozostalo);
    },
    [dismiss]
  );

  /* Pauza przy kursorze i przy fokusie to nie ozdoba: bez niej komunikat ucieka spod kursora
     osobie, ktora wlasnie zaczela go czytac. Powody pauzy moga wystapic DWA naraz (kursor nad
     kafelkiem i fokus w kafelku), wiec trzymamy ZBIOR powodow — odliczanie rusza dopiero, gdy
     zniknie ostatni. Przy jednej fladze zejscie kursora wznawialoby licznik mimo trwajacego
     fokusu, czyli dokladnie pod reka osoby nawigujacej klawiatura. */
  const wstrzymaj = useCallback((id, powod) => {
    const rec = licznikiRef.current.get(id);
    if (!rec) return;
    if (rec.timeoutId) {
      clearTimeout(rec.timeoutId);
      rec.timeoutId = null;
      rec.pozostalo = Math.max(0, rec.pozostalo - (Date.now() - rec.start));
    }
    rec.wstrzymane.add(powod);
  }, []);

  const wznow = useCallback(
    (id, powod) => {
      const rec = licznikiRef.current.get(id);
      if (!rec) return;
      rec.wstrzymane.delete(powod);
      uruchomOdliczanie(id);
    },
    [uruchomOdliczanie]
  );

  /* Liczniki zakladamy i sprzatamy W EFEKCIE, a nie w funkcji pokazujacej komunikat, bo tylko tak
     trzymaja sie tego, co NAPRAWDE jest na ekranie: komunikat wypchniety przez limit `MAX_TOASTS`
     znika z listy nie przechodzac przez `dismiss`, wiec jego licznik zostalby wisiec i po chwili
     probowal zdjac nieistniejacy wpis. */
  useEffect(() => {
    const liczniki = licznikiRef.current;

    Array.from(liczniki.keys()).forEach((id) => {
      if (toasty.some((t) => t.id === id)) return;
      const rec = liczniki.get(id);
      if (rec && rec.timeoutId) clearTimeout(rec.timeoutId);
      liczniki.delete(id);
    });

    toasty.forEach((t) => {
      if (t.leaving || !t.duration) return;
      if (liczniki.has(t.id)) return;
      liczniki.set(t.id, {
        timeoutId: null,
        pozostalo: t.duration,
        start: Date.now(),
        wstrzymane: new Set(),
      });
      uruchomOdliczanie(t.id);
    });
  }, [toasty, uruchomOdliczanie]);

  /* Ostatnia linia obrony przy odmontowaniu (wylogowanie, przeladowanie drzewa): zaden
     `setTimeout` nie moze przezyc komponentu, bo obudzilby sie z `setToasty` na martwym drzewie. */
  useEffect(() => {
    const liczniki = licznikiRef.current;
    const licznikiWyjscia = licznikiWyjsciaRef.current;
    return () => {
      liczniki.forEach((rec) => {
        if (rec.timeoutId) clearTimeout(rec.timeoutId);
      });
      liczniki.clear();
      licznikiWyjscia.forEach((t) => clearTimeout(t));
      licznikiWyjscia.clear();
    };
  }, []);

  const pokaz = useCallback((variant, message, options) => {
    const opcje = options || {};
    const id = kolejneId();
    /* Akcja bez `label` albo bez `onClick` to przycisk, ktorego nie da sie ani przeczytac, ani
       uzyc — lepiej pokazac sam komunikat niz pusty przycisk. */
    const akcja =
      opcje.action && typeof opcje.action.onClick === 'function' && opcje.action.label
        ? { label: opcje.action.label, onClick: opcje.action.onClick }
        : null;

    setToasty((prev) =>
      pushToast(prev, {
        id,
        variant,
        /* Wolajacy czesto podaje `err.message`, ale bywa, ze caly obiekt bledu — kafelek ma pokazac
           tekst, a nie wywrocic render na obiekcie w miejscu dziecka. */
        message: typeof message === 'string' ? message : String(message == null ? '' : message),
        title: opcje.title || '',
        duration: resolveDuration(variant, opcje),
        action: akcja,
        leaving: false,
      })
    );
    return id;
  }, []);

  const api = useMemo(
    () => ({
      success: (message, options) => pokaz('success', message, options),
      error: (message, options) => pokaz('error', message, options),
      warning: (message, options) => pokaz('warning', message, options),
      info: (message, options) => pokaz('info', message, options),
      dismiss,
      dismissAll,
    }),
    [pokaz, dismiss, dismissAll]
  );

  const grzeczne = toasty.filter((t) => !PILNE_WARIANTY.includes(t.variant));
  const pilne = toasty.filter((t) => PILNE_WARIANTY.includes(t.variant));

  const renderujGrupe = (lista) =>
    lista.map((t) => (
      <Komunikat
        key={t.id}
        toast={t}
        onDismiss={dismiss}
        onPause={wstrzymaj}
        onResume={wznow}
        onExited={zdejmijPoAnimacji}
      />
    ));

  /* Portal do `document.body`, bo komunikat ma stac NAD cala aplikacja. Gdyby renderowal sie w
     miejscu wywolania, wystarczyloby jedno `overflow: hidden` albo `transform` na przodku, zeby
     przyciac go do karty albo przestawic uklad odniesienia dla `position: fixed`. */
  const warstwa = (
    <Warstwa data-cy="toast-layer">
      {/* Pilne nizej, czyli blizej dolnej krawedzi, gdzie wzrok trafia najpierw. Podzial na dwie
          grupy wynika z `aria-live` (patrz PILNE_WARIANTY), a nie z estetyki. */}
      <Grupa aria-live="polite" aria-atomic="false" aria-relevant="additions">
        {renderujGrupe(grzeczne)}
      </Grupa>
      <Grupa aria-live="assertive" aria-atomic="false" aria-relevant="additions">
        {renderujGrupe(pilne)}
      </Grupa>
    </Warstwa>
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined' ? ReactDOM.createPortal(warstwa, document.body) : null}
    </ToastContext.Provider>
  );
}

/* ── HOOK ────────────────────────────────────────────────────────────────────────────────────── */

let ostrzezonoOBraku = false;

/* Zestaw awaryjny jest JEDNA staloscia modulu, a nie obiektem tworzonym przy kazdym renderze: hook
   go zwraca, a strony wpisuja wynik do tablic zaleznosci `useEffect`/`useCallback`. Nowy obiekt za
   kazdym razem zapetlalby taki efekt. */
const ZASTEPCZE_API = (() => {
  const wypisz = (poziom, message, options) => {
    const opcje = options || {};
    const id = kolejneId();
    // eslint-disable-next-line no-console
    console[poziom](`[toast] ${opcje.title ? `${opcje.title} — ` : ''}${message}`);
    return id;
  };
  return {
    success: (m, o) => wypisz('log', m, o),
    info: (m, o) => wypisz('log', m, o),
    warning: (m, o) => wypisz('warn', m, o),
    error: (m, o) => wypisz('error', m, o),
    dismiss: () => {},
    dismissAll: () => {},
  };
})();

/**
 * Brak providera NIE moze wywalic strony. Przez cala migracje z `alert()` beda istniec ekrany
 * wolajace hook, zanim `ToastProvider` trafi do `App.js`, a takze testy renderujace pojedyncza
 * strone bez calego drzewa. Zamiast rzucac (co zamienia brakujace opakowanie w bialy ekran),
 * oddajemy zestaw wypisujacy do konsoli — komunikat nie ginie, tylko zmienia miejsce.
 * Ostrzezenie leci RAZ na sesje: przy 58 miejscach wolania kazde wywolanie zasypaloby konsole.
 */
export function useToast() {
  const ctx = useContext(ToastContext);

  useEffect(() => {
    if (ctx || ostrzezonoOBraku) return;
    ostrzezonoOBraku = true;
    // eslint-disable-next-line no-console
    console.warn(
      'useToast(): brak <ToastProvider> nad tym drzewem — komunikaty ida do konsoli. ' +
        'Zamontuj <ToastProvider> w App.js.'
    );
  }, [ctx]);

  return ctx || ZASTEPCZE_API;
}

export default ToastProvider;
